/**
 * LockedTab (Iter 9.39b)
 *
 * Simple password gate used for admin Access Control tab + Portals → Investors
 * tab. Password is checked against env-provided `ADMIN_LOCK_PASSWORD` (set on
 * the backend, verified via /api/admin/verify-lock).
 *
 * Once unlocked the session keeps it unlocked until the tab is remounted or
 * the user signs out.
 */
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session-scoped cache so re-mounting doesn't re-prompt.
const _unlocked = new Set();

export default function LockedTab({ scope = "default", title = "Locked", description, children, testid = "locked-tab" }) {
  const [unlocked, setUnlocked] = useState(_unlocked.has(scope));
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pw) { toast.error("Enter password"); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/verify-lock`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("session_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: pw, scope }),
      });
      const d = await r.json();
      if (!r.ok || !d?.success) { toast.error(d?.detail || "Wrong password"); return; }
      _unlocked.add(scope);
      setUnlocked(true);
      setPw("");
      toast.success("Unlocked");
    } catch (err) {
      toast.error(String(err.message || err));
    } finally { setBusy(false); }
  };

  if (unlocked) return <>{children}</>;

  return (
    <Card data-testid={testid}>
      <CardContent className="p-8 max-w-md mx-auto space-y-4 text-center">
        <div className="w-12 h-12 mx-auto rounded-full bg-gray-900 text-white flex items-center justify-center">
          <Lock className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-base font-bold">{title}</h3>
          {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
        </div>
        <form onSubmit={submit} className="space-y-2">
          <div className="relative">
            <Input
              type={show ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Enter admin password"
              className="pr-10"
              data-testid={`${testid}-input`}
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-700"
              data-testid={`${testid}-toggle-visibility`}
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-gray-900 hover:bg-black" data-testid={`${testid}-submit`}>
            {busy ? "Verifying…" : "Unlock"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
